import React, { useCallback } from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { ChevronDown, ChevronRight, Edit2, Check, X, Plus, Trash2 } from 'lucide-react';
import { type RegionNode } from '../../../api/regions';

export interface RegionRowProps {
  node: RegionNode;
  depth: number;
  expanded: Set<string>;
  onToggle: (id: string) => void;
  editingId: string | null;
  editName: string;
  onEditStart: (id: string, name: string) => void;
  onEditChange: (name: string) => void;
  onEditSave: (id: string) => void;
  onEditCancel: () => void;
  addingChildOf: string | null;
  newChildName: string;
  onAddChildStart: (id: string) => void;
  onNewChildNameChange: (name: string) => void;
  onAddChildSave: (parentId: string) => void;
  onAddChildCancel: () => void;
  onDelete: (id: string, name: string) => void;
  colorMap: Map<string, string>;
  draggingId: string | null;
  isValidDrop: (draggedId: string, targetId: string) => boolean;
}

function RegionRow({
  node, depth, expanded, onToggle,
  editingId, editName, onEditStart, onEditChange, onEditSave, onEditCancel,
  addingChildOf, newChildName, onAddChildStart, onNewChildNameChange, onAddChildSave, onAddChildCancel,
  onDelete, colorMap,
  draggingId, isValidDrop,
}: RegionRowProps) {
  const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({ id: node.id });
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: node.id });

  // Combine drag and drop refs onto the same element
  const setNodeRef = useCallback((el: HTMLDivElement | null) => {
    setDragRef(el);
    setDropRef(el);
  }, [setDragRef, setDropRef]);

  const isExpanded = expanded.has(node.id);
  const isEditing = editingId === node.id;
  const isAddingChild = addingChildOf === node.id;
  const color = colorMap.get(node.id) || '#6b7280';
  const hasChildren = node.children.length > 0;
  const indent = depth * 16;

  const canReceiveDrop = draggingId !== null && isValidDrop(draggingId, node.id);
  const isDropTarget = isOver && canReceiveDrop;

  // ---- Event handlers ----

  function handleToggle() {
    onToggle(node.id);
  }

  function handleEditChange(e: React.ChangeEvent<HTMLInputElement>) {
    onEditChange(e.target.value);
  }

  function handleEditKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') onEditSave(node.id);
    if (e.key === 'Escape') onEditCancel();
  }

  function handleEditSaveClick() {
    onEditSave(node.id);
  }

  function handleAddChildClick() {
    onAddChildStart(node.id);
  }

  function handleEditStartClick() {
    onEditStart(node.id, node.name);
  }

  function handleDeleteClick() {
    onDelete(node.id, node.name);
  }

  function handleChildNameChange(e: React.ChangeEvent<HTMLInputElement>) {
    onNewChildNameChange(e.target.value);
  }

  function handleChildKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') onAddChildSave(node.id);
    if (e.key === 'Escape') onAddChildCancel();
  }

  function handleAddChildSaveClick() {
    onAddChildSave(node.id);
  }

  return (
    <div>
      <div
        ref={setNodeRef}
        {...attributes}
        {...listeners}
        className={[
          'flex items-center gap-1 py-2 px-2 rounded-lg group transition-colors select-none',
          isDragging ? 'opacity-40' : '',
          isDropTarget
            ? 'ring-2 ring-inset ring-moss-400 dark:ring-moss-500 bg-moss-50 dark:bg-moss-900/20'
            : (draggingId !== null && canReceiveDrop)
              ? 'hover:ring-2 hover:ring-inset hover:ring-moss-300 dark:hover:ring-moss-600 hover:bg-stone-50 dark:hover:bg-[#242d28]'
              : 'hover:bg-stone-100 dark:hover:bg-[#242d28]',
          draggingId !== null && !isDragging
            ? (canReceiveDrop ? 'cursor-copy' : 'cursor-not-allowed')
            : 'cursor-grab',
        ].filter(Boolean).join(' ')}
        style={{ paddingLeft: `${8 + indent}px` }}
      >
        {/* Expand/collapse */}
        <button
          className="w-4 h-4 flex-shrink-0 text-stone-400"
          onClick={handleToggle}
        >
          {hasChildren
            ? (isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />)
            : <span className="w-3.5 inline-block" />
          }
        </button>

        {/* Color dot */}
        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />

        {/* Name / edit input */}
        {isEditing ? (
          <input
            autoFocus
            className="flex-1 text-sm px-1 py-0.5 rounded border border-moss-400 dark:border-moss-600 bg-white dark:bg-[#242d28] text-stone-900 dark:text-stone-100 min-w-0"
            value={editName}
            onChange={handleEditChange}
            onKeyDown={handleEditKeyDown}
          />
        ) : (
          <span className="flex-1 text-sm text-stone-800 dark:text-stone-200 truncate">
            {node.name}
          </span>
        )}

        {/* Venue count */}
        {!isEditing && (
          <span className="text-xs text-stone-400 dark:text-stone-500 flex-shrink-0 ml-1">
            {node.venueCount > 0 ? `${node.venueCount} venue${node.venueCount !== 1 ? 's' : ''}` : ''}
          </span>
        )}

        {/* Action buttons */}
        {isEditing ? (
          <div className="flex gap-1 flex-shrink-0">
            <button
              className="p-1 rounded text-moss-600 hover:text-moss-800 hover:bg-moss-100 dark:text-moss-400 dark:hover:bg-moss-900/30 transition-colors"
              onClick={handleEditSaveClick}
              title="Save"
            >
              <Check size={14} />
            </button>
            <button
              className="p-1 rounded text-stone-400 hover:text-stone-700 hover:bg-stone-200 dark:hover:bg-stone-700 transition-colors"
              onClick={onEditCancel}
              title="Cancel"
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          <div className="flex gap-0.5 flex-shrink-0">
            <button
              className="p-1 rounded text-stone-400 hover:text-moss-600 hover:bg-stone-200 dark:hover:text-moss-400 dark:hover:bg-stone-700 transition-colors"
              onClick={handleAddChildClick}
              title="Add child region"
            >
              <Plus size={14} />
            </button>
            <button
              className="p-1 rounded text-stone-400 hover:text-stone-700 hover:bg-stone-200 dark:hover:text-stone-200 dark:hover:bg-stone-700 transition-colors"
              onClick={handleEditStartClick}
              title="Rename"
            >
              <Edit2 size={14} />
            </button>
            <button
              className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              onClick={handleDeleteClick}
              title="Delete"
            >
              <Trash2 size={14} />
            </button>
          </div>
        )}
      </div>

      {/* Add child form */}
      {isAddingChild && (
        <div
          className="flex items-center gap-1.5 px-2 py-1.5"
          style={{ paddingLeft: `${8 + indent + 20}px` }}
        >
          <input
            autoFocus
            className="flex-1 text-sm px-2 py-1 rounded border border-moss-400 dark:border-moss-600 bg-white dark:bg-[#242d28] text-stone-900 dark:text-stone-100"
            placeholder="Child region name…"
            value={newChildName}
            onChange={handleChildNameChange}
            onKeyDown={handleChildKeyDown}
          />
          <button
            className="p-1 text-moss-600 hover:text-moss-800 dark:text-moss-400"
            onClick={handleAddChildSaveClick}
          >
            <Check size={14} />
          </button>
          <button
            className="p-1 text-stone-400 hover:text-stone-600"
            onClick={onAddChildCancel}
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Children */}
      {isExpanded && node.children.length > 0 && (
        <div>
          {node.children.map((child) => (
            <RegionRow
              key={child.id}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              onToggle={onToggle}
              editingId={editingId}
              editName={editName}
              onEditStart={onEditStart}
              onEditChange={onEditChange}
              onEditSave={onEditSave}
              onEditCancel={onEditCancel}
              addingChildOf={addingChildOf}
              newChildName={newChildName}
              onAddChildStart={onAddChildStart}
              onNewChildNameChange={onNewChildNameChange}
              onAddChildSave={onAddChildSave}
              onAddChildCancel={onAddChildCancel}
              onDelete={onDelete}
              colorMap={colorMap}
              draggingId={draggingId}
              isValidDrop={isValidDrop}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default RegionRow;
